@if (toggle && featureStatus) {
  <div class="chart-block">
    @if (chart.disclaimer) {
      <span class="poi-disclaimer">{{ chart.disclaimer }}</span>
    }

    <!-- The switch works whether or not the chart is paid for: opening a chart
         is how someone finds out what it holds, and the paywall sits on the
         chart itself. -->
    <app-slide-toggle
      class="menu-toggle"
      [formControl]="toggle"
      [disabled]="disableToggle"
      [ngClass]="disableToggle ? 'disabled' : ''"
      [attr.data-cy]="'toggle-' + chart.e2eName"
      >{{ chart.toggleTitle }}

      @if (chart.tooltip || lock) {
        <app-feature-tooltip
          [headerText]="chart.tooltip ? chart.tooltip.headerText : ''"
          [isFeatureInaccessibleType]="!!lock"
          [tooltipText]="chart.tooltip ? chart.tooltip.text : ''"
          [featureConst]="chart.featureConst"
          [featureName]="chart.toggleTitle"
          (openTooltipModal)="handleTooltipModalOpen($event)"
        ></app-feature-tooltip>
      }
    </app-slide-toggle>

    <!-- Nothing to pick a year for while the numbers are behind the blur. -->
    @if (!lock && chart.accessibleYears && chart.accessibleYears.length > 0) {
      <app-chart-year-selection
        [isChartVisible]="featureStatus!.isSelected && toggle && toggle!.value"
        [selectedYear]="$any(featureStatus)![chart.yearVariable!]"
        [accessibleYears]="chart.accessibleYears"
        [usedFor]="chart.yearVariable!"
        (selectedYearChange)="onSelectedYearChange($event)"
      ></app-chart-year-selection>
    }

    <!-- A reselect sets isLoading again while this chart's previous data is
         still sitting in featureStatus - so once there is a chart to show,
         a reload plays as an overlay on top of it instead of pulling it out
         from under the visitor. Only a first-ever load, with nothing drawn
         yet, falls back to the plain placeholder below. -->
    @if (
      toggle.value &&
      featureStatus!.isSelected &&
      $any(featureStatus)[chart.containsChartDataVariable]
    ) {
      <div class="chart-wrap">
        @if (lock) {
          <!-- The chart itself, blurred: the shape of the answer is the tease,
               the numbers are what is being sold. The gate line sits directly on
               top of it so the ask is read before the blur is. -->
          <div
            class="locked"
            [class.locked--free]="lock.free"
            (click)="requestUnlock()"
            data-cy="chart-paywall"
          >
            <div class="locked__gate">
              <svg class="locked__lock" viewBox="0 0 12 16" aria-hidden="true">
                <path
                  d="M1.45 15.48c-.4 0-.74-.14-1.02-.41A1.35 1.35 0 0 1 0 14.09V6.87c0-.39.14-.71.42-.98.29-.28.63-.41 1.03-.41H2.4V3.94c0-.96.35-1.78 1.05-2.45A3.5 3.5 0 0 1 6 .48c1 0 1.85.34 2.55 1.01.7.67 1.05 1.49 1.05 2.45v1.54h.95c.4 0 .74.13 1.02.41.29.27.43.59.43.98v7.22c0 .38-.14.7-.43.98-.28.27-.62.41-1.02.41H1.45Zm0-1.15h9.1c.08 0 .14-.03.18-.07a.23.23 0 0 0 .07-.17V6.87a.23.23 0 0 0-.07-.17.25.25 0 0 0-.18-.07h-9.1a.25.25 0 0 0-.18.07.23.23 0 0 0-.07.17v7.22c0 .07.02.13.07.17.04.04.1.07.18.07ZM6 11.82c.39 0 .72-.13.99-.39.27-.26.41-.58.41-.95 0-.38-.14-.7-.41-.96A1.37 1.37 0 0 0 6 9.13c-.39 0-.72.13-.99.39-.27.26-.41.58-.41.96 0 .37.14.69.41.95.27.26.6.39.99.39ZM3.6 5.48h4.8V3.94c0-.65-.23-1.19-.7-1.64A2.4 2.4 0 0 0 6 1.63c-.67 0-1.23.22-1.7.67-.47.45-.7.99-.7 1.64v1.54Z"
                  fill="currentColor"
                />
              </svg>
              <span class="locked__note">{{ lock.note }}</span>
              <button
                type="button"
                class="gradient-button locked__cta"
                [attr.aria-label]="
                  lock.aria + ' the ' + chart.chartName + ' chart'
                "
              >
                {{ lock.cta }}
              </button>
            </div>

            <div class="locked__chart" aria-hidden="true">
              <ng-container *ngTemplateOutlet="chartCanvas"></ng-container>
            </div>
          </div>
        } @else {
          <div class="chart">
            <ng-container *ngTemplateOutlet="chartCanvas"></ng-container>
          </div>
        }

        @if (featureStatus.isLoading) {
          <div class="chart-reload" data-cy="chart-skeleton">
            <span class="visually-hidden"
              >Loading the {{ chart.chartName }} chart</span
            >
          </div>
        }
      </div>
    } @else if (toggle.value && featureStatus.isLoading) {
      <div
        class="chart-skeleton"
        [style.height]="skeletonHeight"
        data-cy="chart-skeleton"
      >
        <span class="visually-hidden"
          >Loading the {{ chart.chartName }} chart</span
        >
      </div>
    }

    <app-chart-warning
      [selectedYear]="
        wasYearSelected ? $any(featureStatus)![chart.yearVariable!] : undefined
      "
      [selected]="featureStatus.isSelected"
      [containsInfo]="$any(featureStatus)[chart.containsChartDataVariable]"
      [isLoading]="!!featureStatus.isLoading"
      [isToggleActive]="toggle && toggle.value"
      [noAccessibleYears]="chart.accessibleYears?.length === 0"
      [chartName]="chart.chartName"
    ></app-chart-warning>
  </div>
}

<!-- One definition for both renderings: locked only changes what is wrapped
     around the chart, never the chart. -->
<ng-template #chartCanvas>
  <app-plotly-chart
    [naturalId]="featureStatus!.naturalId"
    [selectedYear]="$any(featureStatus)![chart.yearVariable!]"
    [chart]="
      chart.plotlyChart ||
      chart.chartsByYears![$any(featureStatus)[$any(chart.yearVariable)]]
    "
    [chartStyle]="chart.chartStyle || defaultChartStyle"
    [chartName]="chart.chartName"
    [e2eName]="chart.e2eName"
    [featureConst]="chart.featureConst"
  ></app-plotly-chart>
</ng-template>
